import React from 'react';
import dayjs from 'dayjs';
import { Icon } from '../absence-history/index.sc';
import BluePen from '../../images/blue-pen.svg';
import { Popup } from '@progress/kendo-react-popup';
import { Link } from 'react-router-dom';
import { enums } from '../../utils';
import Button from '../base/button';
import LoadingIndicatorCard from './loading-indicator-card';
import useMobileView from '../../hooks/use-mobile-view';
import {
  baseWeekCount,
  buildLinearBars,
  findMilestoneDate,
  getDomainBounds,
  getDurationDays,
  getBarLetter,
  getPct,
  getRangeBounds,
  getSummaryMetrics,
  getTimelineBounds,
  getUnifiedTimelineBounds,
  handleLeavePlanInit,
  hasTimelineData,
  mobileBarMinHeightPx,
  mobileLaneWidthPx,
  mobileLargestBarMinHeightPx,
  mobileWeekConnectorHeightPx,
  mobileWeekInnerGapPx,
  mobileWeekLabelHeightPx,
  mobileWeekOuterGapPx,
  positionMobileTimelineBars,
  timelineGutterPx,
  timelineInnerGutterPx,
  timelineLegendDefinitions,
  timelineRowSpacingPx,
  timelineViewportHeight,
  toValidDay,
  MobileLinearBar,
} from './leave-plan-helpers';
import * as dm from '../../data-models';
import {
  CalendarShell,
  Container,
  Header,
  HeaderRow,
  IconButtonSingle,
  Metric,
  MetricLabel,
  MetricValue,
  MetricsRow,
  MobileTimelineBadge,
  MobileTimelineDate,
  MobileTimelineItem,
  MobileTimelineList,
  MobileTimelineText,
  MobileTimelineTitle,
  MobileTimelineTrack,
  PageWrapper,
  PopupContent,
  TimelineBar,
  TimelineGrid,
  TimelineLegend,
  TimelineLegendItem,
  TimelineLegendSwatch,
  TimelineMarker,
  WeekCell,
  WeekRow,
} from './tailwind-components';

interface LeavePlanProps {
  systemId?: string;
  keyId?: string;
  visualType: 'intake' | 'continuous';
  moveToStep?: Function;
  editConfig?: any;
  draftClaim?: dm.DraftClaim;
  leaves?: dm.Leave[];
  claims?: dm.Claim[];
  calendarData?: dm.Calendar[];
}

const LeavePlan: (p: LeavePlanProps) => React.ReactElement = (p) => {
  const [displayEdit, setDisplayEdit] = React.useState(false);
  const [rawElements, setRawElements] = React.useState<any>([]);
  const [periods, setPeriods] = React.useState<any>([]);
  const [benefits, setBenefits] = React.useState<any>([]);
  const [loading, setLoading] = React.useState(true);
  const anchor = React.useRef<HTMLButtonElement | null>(null);
  const isMobileView = useMobileView(639);

  const showEditPopup = (e: any) => {
    e.preventDefault();
    setDisplayEdit(!displayEdit);
  };

  const edit = (index: number, elementName: string) => {
    anchor.current?.focus({ preventScroll: true });
    setDisplayEdit(false);
    p?.moveToStep?.({ value: index }, elementName);
  };

  React.useEffect(() => {
    let ignore = false;
    handleLeavePlanInit({
      draftClaim: p.draftClaim,
      setLoading: (v) => {
        if (!ignore) setLoading(v);
      },
      setRawElements: (v) => {
        if (!ignore) setRawElements(v);
      },
      setPeriods: (v) => {
        if (!ignore) setPeriods(v);
      },
      setBenefits: (v) => {
        if (!ignore) setBenefits(v);
      },
    });
    return () => {
      ignore = true;
    };
  }, [p.draftClaim]);

  const editConfig = p?.editConfig ?? {};
  const summary = getSummaryMetrics(periods);

  const timelineBars = React.useMemo(
    () => buildLinearBars(rawElements),
    [rawElements],
  );
  const visibleTimelineLegends = React.useMemo(() => {
    const generatedBarTypes = new Set(timelineBars.map((bar) => bar.styleType));
    return timelineLegendDefinitions.filter((legend) =>
      generatedBarTypes.has(legend.styleType),
    );
  }, [timelineBars]);
  const mobileTimelineBars = React.useMemo<MobileLinearBar[]>(
    () =>
      timelineBars.map((bar, index) => ({
        ...bar,
        durationDays: getDurationDays(bar.startDate, bar.endDate),
        letter: getBarLetter(index),
      })),
    [timelineBars],
  );
  const mobilePositionedBars = React.useMemo(
    () => positionMobileTimelineBars(mobileTimelineBars),
    [mobileTimelineBars],
  );
  const timelineBounds = React.useMemo(
    () => getTimelineBounds(timelineBars),
    [timelineBars],
  );
  const periodBounds = React.useMemo(() => getRangeBounds(periods), [periods]);
  const dueDate = findMilestoneDate(rawElements, ['due date']);
  const leaveEnd = findMilestoneDate(rawElements, [
    'requested leave end date',
    'leave end',
    'job protected end date',
  ]);
  const dueDateDay = React.useMemo(() => toValidDay(dueDate), [dueDate]);
  const leaveEndDay = React.useMemo(() => toValidDay(leaveEnd), [leaveEnd]);
  const domainBounds = React.useMemo(
    () =>
      getDomainBounds([
        periodBounds.start,
        periodBounds.end,
        timelineBounds.start,
        timelineBounds.end,
        dueDateDay,
        leaveEndDay,
      ]),
    [
      dueDateDay,
      leaveEndDay,
      periodBounds.end,
      periodBounds.start,
      timelineBounds.end,
      timelineBounds.start,
    ],
  );
  const timelineStart = domainBounds.start;
  const timelineEnd = domainBounds.end;
  const unifiedTimelineBounds = React.useMemo(
    () =>
      getUnifiedTimelineBounds({
        rangeStart: timelineStart,
        rangeEnd: timelineEnd,
        dueDate,
        leaveEnd,
      }),
    [dueDate, leaveEnd, timelineEnd, timelineStart],
  );
  const timelineRangeStart = unifiedTimelineBounds.start;
  const timelineRangeEnd = unifiedTimelineBounds.end;
  const timelineWeeks = React.useMemo(() => {
    if (!timelineRangeStart || !timelineRangeEnd) {
      return baseWeekCount;
    }
    const totalDays = Math.max(
      1,
      timelineRangeEnd.diff(timelineRangeStart, 'day') + 1,
    );
    return Math.max(1, Math.ceil(totalDays / 7));
  }, [timelineRangeEnd, timelineRangeStart]);
  const timelineCanvasWidthPercent = React.useMemo(
    () => Math.max(100, (timelineWeeks / baseWeekCount) * 100),
    [timelineWeeks],
  );
  const timelineCanvasHeight = React.useMemo(() => {
    const rowsHeight = timelineBars.length * timelineRowSpacingPx;
    return Math.max(timelineViewportHeight, rowsHeight + 100);
  }, [timelineBars.length]);
  const mobileTimelineBaseScaleHeightPx = React.useMemo(() => {
    if (timelineWeeks <= 0) {
      return mobileWeekLabelHeightPx;
    }

    const connectorSegments = Math.max(0, timelineWeeks - 1);
    return (
      timelineWeeks * mobileWeekLabelHeightPx +
      connectorSegments *
        (mobileWeekInnerGapPx +
          mobileWeekConnectorHeightPx +
          mobileWeekOuterGapPx)
    );
  }, [timelineWeeks]);
  const mobileTimelineTotalDays = React.useMemo(() => {
    if (!timelineRangeStart || !timelineRangeEnd) {
      return 1;
    }

    return Math.max(1, timelineRangeEnd.diff(timelineRangeStart, 'day') + 1);
  }, [timelineRangeEnd, timelineRangeStart]);
  const mobileLaneCount = React.useMemo(
    () => mobilePositionedBars.laneCount,
    [mobilePositionedBars],
  );
  const mobileLaneCanvasWidthPx = React.useMemo(
    () => mobileLaneCount * mobileLaneWidthPx,
    [mobileLaneCount],
  );
  const mobileMaxBarDurationDays = React.useMemo(
    () => Math.max(1, ...mobileTimelineBars.map((bar) => bar.durationDays)),
    [mobileTimelineBars],
  );
  const mobileTimelineScaleHeightPx = React.useMemo(() => {
    const requiredHeightForLargestBar = Math.ceil(
      (mobileLargestBarMinHeightPx * mobileTimelineTotalDays) /
        mobileMaxBarDurationDays,
    );

    return Math.max(
      mobileTimelineBaseScaleHeightPx,
      requiredHeightForLargestBar,
    );
  }, [
    mobileMaxBarDurationDays,
    mobileTimelineBaseScaleHeightPx,
    mobileTimelineTotalDays,
  ]);

  const getMarkerLeft = (dateStr?: string) => {
    if (!timelineRangeStart || !timelineRangeEnd || !dateStr) {
      return null;
    }
    return getPct(timelineRangeStart, timelineRangeEnd, dayjs(dateStr));
  };
  const markerAlign = (value: number): 'left' | 'center' | 'right' => {
    if (value <= 10) return 'left';
    if (value >= 90) return 'right';
    return 'center';
  };
  const dueDateLeft = getMarkerLeft(dueDate);
  const leaveEndLeft = getMarkerLeft(leaveEnd);

  const timelineHasData = hasTimelineData({
    rawElements,
    periods,
    benefits: {
      jobProtectionBenefits: benefits?.jobProtectionBenefits ?? [],
      incomeProtectionBenefits: benefits?.incomeProtectionBenefits ?? [],
    },
  });

  if (loading || (!p.draftClaim && !timelineHasData)) {
    return <LoadingIndicatorCard />;
  }

  if (!timelineHasData && !p.draftClaim) {
    return (
      <PageWrapper>
        <Container id="leave-plan-container">
          <div id="leave-plan-fetch-error" className="info-bubble">
            <p>
              <strong>
                We are unable to load your leave plan details right now.
              </strong>
            </p>
            <p>Please click "Next" to proceed.</p>
          </div>
        </Container>
      </PageWrapper>
    );
  }

  if (
    p.visualType === 'intake' &&
    benefits?.jobProtectionBenefits?.length === 0 &&
    benefits?.incomeProtectionBenefits?.length === 0
  ) {
    return (
      <div className="info-bubble">
        <p>
          <strong>Your leave plan was built successfully!</strong>
        </p>
        <p>Please click "Next" to proceed.</p>
      </div>
    );
  }

  return (
    <PageWrapper>
      <Container id="leave-plan-container">
        <HeaderRow>
          <Header>
            {p.visualType === 'intake'
              ? 'Your Estimated Leave Plan'
              : 'Your Leave Calendar'}
          </Header>
          {editConfig?.allowEdit === 'true' && (
            <IconButtonSingle
              id="lpv-edit-button"
              onClick={showEditPopup}
              ref={anchor}
            >
              <Icon src={BluePen} alt="" height="16px" width="16px" /> Edit
            </IconButtonSingle>
          )}
        </HeaderRow>

        <MetricsRow>
          <Metric>
            <MetricLabel>Start Date:</MetricLabel>
            <MetricValue>{summary.startDate ?? '--'}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>End Date:</MetricLabel>
            <MetricValue>{summary.endDate ?? '--'}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Leave Length:</MetricLabel>
            <MetricValue>{summary.leaveLength ?? '--'}</MetricValue>
          </Metric>
        </MetricsRow>

        <CalendarShell>
          {isMobileView ? (
            <div
              id="leave-plan-mobile-timeline"
              data-testid="leave-plan-mobile-timeline"
              className="tw-overflow-x-auto tw-overflow-y-hidden tw-pb-2"
            >
              <div className="tw-flex tw-min-w-max tw-gap-4">
                <div
                  className="tw-flex tw-w-[56px] tw-flex-col tw-items-center tw-shrink-0 tw-pt-1"
                  style={{ rowGap: `${mobileWeekOuterGapPx}px` }}
                >
                  {Array.from({ length: timelineWeeks }).map((_, weekIndex) => {
                    return (
                      <div
                        key={`mobile-week-${weekIndex + 1}`}
                        className="tw-flex tw-flex-col tw-items-center"
                        style={{ rowGap: `${mobileWeekInnerGapPx}px` }}
                      >
                        <span className="tw-text-[10px] tw-leading-3 tw-text-[#4e4e4e] tw-font-sans tw-whitespace-nowrap">
                          wk {weekIndex + 1}
                        </span>
                        {weekIndex < timelineWeeks - 1 && (
                          <span
                            aria-hidden="true"
                            className="tw-w-px tw-bg-[#d0d5dd]"
                            style={{
                              height: `${mobileWeekConnectorHeightPx}px`,
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="tw-flex tw-min-w-0 tw-flex-1">
                  <div
                    className="tw-relative tw-pr-1"
                    style={{
                      width: `${mobileLaneCanvasWidthPx}px`,
                      height: `${mobileTimelineScaleHeightPx}px`,
                    }}
                  >
                    {mobilePositionedBars.positionedBars.map(
                      (row, rowIndex) => {
                        const startOffsetDays = timelineRangeStart
                          ? Math.max(
                              0,
                              dayjs(row.startDate).diff(
                                timelineRangeStart,
                                'day',
                              ),
                            )
                          : 0;
                        const top = Math.round(
                          (startOffsetDays / mobileTimelineTotalDays) *
                            mobileTimelineScaleHeightPx,
                        );
                        const barHeight = Math.max(
                          mobileBarMinHeightPx,
                          Math.round(
                            (row.durationDays / mobileTimelineTotalDays) *
                              mobileTimelineScaleHeightPx,
                          ),
                        );
                        const clampedTop = Math.max(
                          0,
                          Math.min(
                            top,
                            Math.max(
                              0,
                              mobileTimelineScaleHeightPx -
                                mobileBarMinHeightPx,
                            ),
                          ),
                        );
                        const maxHeight =
                          mobileTimelineScaleHeightPx - clampedTop;
                        const clampedHeight = Math.max(
                          mobileBarMinHeightPx,
                          Math.min(maxHeight, barHeight),
                        );
                        const lane = row.lane;
                        const left = lane * mobileLaneWidthPx;

                        return (
                          <div
                            key={row.letter}
                            className="tw-absolute"
                            style={{
                              left: `${left}px`,
                              top: `${clampedTop}px`,
                            }}
                          >
                            <MobileTimelineItem
                              className="tw-py-0"
                              key={`mobile-lane-${row.letter}`}
                            >
                              <MobileTimelineTrack
                                data-testid={`leave-plan-mobile-bar-${rowIndex}-${row.letter}`}
                                data-bar-id={row.id}
                                data-related-to={row.relatedToId ?? ''}
                                data-style-type={row.styleType}
                                swatchType={row.styleType}
                                height={clampedHeight}
                                letter={row.letter}
                              />
                            </MobileTimelineItem>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              id="leave-plan-linear-calendar"
              data-testid="leave-plan-linear-calendar"
            >
              <TimelineGrid
                canvasWidthPercent={timelineCanvasWidthPercent}
                canvasHeightPx={timelineCanvasHeight}
                weekCount={timelineWeeks}
              >
                {dueDateLeft !== null && (
                  <TimelineMarker
                    left={`calc(${timelineGutterPx}px + (100% - ${timelineInnerGutterPx}px) * ${
                      dueDateLeft / 100
                    })`}
                    date={dueDate ? dayjs(dueDate).format('MM/DD') : '--/--'}
                    label="Due Date"
                    align={markerAlign(dueDateLeft)}
                  />
                )}
                {leaveEndLeft !== null && (
                  <TimelineMarker
                    left={`calc(${timelineGutterPx}px + (100% - ${timelineInnerGutterPx}px) * ${
                      leaveEndLeft / 100
                    })`}
                    date={leaveEnd ? dayjs(leaveEnd).format('MM/DD') : '--/--'}
                    label="Leave ends"
                    align={markerAlign(leaveEndLeft)}
                    reverseLabel
                  />
                )}
                {timelineRangeStart &&
                  timelineRangeEnd &&
                  timelineBars.map((bar, index) => {
                    const start = dayjs(bar.startDate);
                    const end = dayjs(bar.endDate);
                    const startLabel = start.format('MM/DD/YYYY');
                    const endLabel = end.format('MM/DD/YYYY');
                    const dateRange = `${startLabel} - ${endLabel}`;
                    const dataLeft = getPct(
                      timelineRangeStart,
                      timelineRangeEnd,
                      start,
                    );
                    const dataRight = getPct(
                      timelineRangeStart,
                      timelineRangeEnd,
                      end.add(1, 'day'),
                    );
                    const dataWidth = Math.max(0, dataRight - dataLeft);
                    const left = `calc(${timelineGutterPx}px + (100% - ${timelineInnerGutterPx}px) * ${
                      dataLeft / 100
                    })`;
                    const width = `calc((100% - ${timelineInnerGutterPx}px) * ${Math.max(
                      0.02,
                      dataWidth / 100,
                    )})`;

                    return (
                      <TimelineBar
                        key={bar.id}
                        left={left}
                        width={width}
                        top={44 + index * timelineRowSpacingPx}
                        barTestId={`leave-plan-desktop-bar-${index}`}
                        barId={bar.id}
                        relatedToId={bar.relatedToId}
                        styleType={bar.styleType}
                        label={bar.label}
                        dateRange={dateRange}
                      />
                    );
                  })}
                <WeekRow
                  weekCount={timelineWeeks}
                  leadingBufferPx={timelineGutterPx}
                  trailingBufferPx={timelineGutterPx}
                >
                  {Array.from({ length: timelineWeeks }).map((_, i) => (
                    <WeekCell key={`wk-${i + 1}`}>wk {i + 1}</WeekCell>
                  ))}
                </WeekRow>
              </TimelineGrid>
            </div>
          )}

          {visibleTimelineLegends.length > 0 && (
            <TimelineLegend>
              {visibleTimelineLegends.map((legend) => (
                <TimelineLegendItem key={`legend-${legend.styleType}`}>
                  <TimelineLegendSwatch swatchType={legend.styleType} />{' '}
                  {legend.label}
                </TimelineLegendItem>
              ))}
            </TimelineLegend>
          )}

          <MobileTimelineList id="leave-plan-mobile-item-list">
            {mobileTimelineBars.map((row) => (
              <div key={row.letter} className="tw-flex tw-items-start tw-gap-3">
                <MobileTimelineBadge>{row.letter}</MobileTimelineBadge>
                <MobileTimelineText className="tw-flex-1">
                  <MobileTimelineTitle>{row.label}</MobileTimelineTitle>
                  <MobileTimelineDate>
                    {`${dayjs(row.startDate).format('MM/DD/YY')} - ${dayjs(
                      row.endDate,
                    ).format('MM/DD/YY')}`}
                  </MobileTimelineDate>
                </MobileTimelineText>
              </div>
            ))}
          </MobileTimelineList>
        </CalendarShell>

        <EditPopover
          anchor={anchor}
          editConfig={editConfig}
          edit={edit}
          displayEdit={displayEdit}
          visualType={p.visualType}
        />
      </Container>
    </PageWrapper>
  );
};

interface IEditPopover {
  anchor: React.MutableRefObject<HTMLButtonElement | null>;
  editConfig: any;
  edit: (index: number, elementName: string) => void;
  displayEdit: boolean;
  visualType: string;
}

const EditPopover: (p: IEditPopover) => React.ReactElement = (p) => {
  const { anchor, editConfig, edit, displayEdit, visualType } = p;
  return (
    <Popup
      id="lpv-edit-popover"
      animate={false}
      anchor={anchor.current}
      anchorAlign={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      popupAlign={{
        horizontal: 'right',
        vertical: 'top',
      }}
      show={displayEdit}
      popupClass={'popup-content'}
      margin={{ vertical: 5, horizontal: 0 }}
      style={{
        boxShadow: '0px 3px 6px #00000029',
        borderRadius: '8px',
      }}
    >
      {visualType === 'intake' ? (
        <PopupContent id="lpv-intake-popover-content">
          {editConfig?.editLinks?.map(
            (
              option: {
                linkText: string;
                formStepIndex: number;
                formFieldName: string;
              },
              i: number,
            ) => (
              <Button
                key={`edit-option-${option.formFieldName}-${i}`}
                id={`lpv-edit-option-${i}`}
                buttonStyle={enums.ButtonTypes.link}
                style={{ textAlign: 'left' }}
                onClick={() => edit(option.formStepIndex, option.formFieldName)}
              >
                {option.linkText}
              </Button>
            ),
          )}
        </PopupContent>
      ) : (
        <PopupContent id="lpv-ocm-popover-content">
          <Link to={`${enums.AppPaths.form}/last-day-worked`}>
            Revise your last day worked
          </Link>
          <Link to={`${enums.AppPaths.form}/return-to-work`}>
            Set a new return to work date
          </Link>
          <Link to={`${enums.AppPaths.form}/delivery-details`}>
            Move your delivery date
          </Link>
        </PopupContent>
      )}
    </Popup>
  );
};

export default LeavePlan;
